import { apiRequest } from './client';

const SF_API_VERSION = 'v59.0';
import type { SalesforceFieldDescribe, SalesforceObjectDescribe } from '../../types/salesforce';

// In-memory cache for object describes
const describeCache = new Map<string, SalesforceObjectDescribe>();

/**
 * Get the full field describe for a Salesforce object.
 * Results are cached in memory for the session.
 */
export async function describeObject(
  objectName: string,
  signal?: AbortSignal
): Promise<SalesforceObjectDescribe> {
  const cached = describeCache.get(objectName);
  if (cached) return cached;

  const response = await apiRequest(
    `/services/data/${SF_API_VERSION}/sobjects/${objectName}/describe`,
    {},
    signal
  );

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Describe ${objectName} failed: ${errorText}`);
  }

  const data = await response.json();
  const result: SalesforceObjectDescribe = {
    name: data.name,
    label: data.label,
    labelPlural: data.labelPlural,
    fields: data.fields,
  };

  describeCache.set(objectName, result);
  return result;
}

/**
 * Get queryable field names for a Salesforce object.
 * Filters out compound fields and non-queryable types.
 */
export async function getQueryableFields(
  objectName: string,
  signal?: AbortSignal
): Promise<SalesforceFieldDescribe[]> {
  const describe = await describeObject(objectName, signal);

  // Filter to queryable fields, exclude compound address/geolocation
  const excludeTypes = new Set(['address', 'location']);
  return describe.fields.filter(
    (f) => !excludeTypes.has(f.type) && f.name !== 'attributes'
  );
}

/**
 * Build a SELECT query for all queryable fields of an object.
 */
export async function buildSelectAllQuery(
  objectName: string,
  whereClause?: string,
  signal?: AbortSignal
): Promise<string> {
  const fields = await getQueryableFields(objectName, signal);
  const fieldNames = fields.map((f) => f.name).join(', ');
  let query = `SELECT ${fieldNames} FROM ${objectName}`;
  if (whereClause) {
    query += ` WHERE ${whereClause}`;
  }
  return query;
}

/**
 * Get the active picklist values for a single field on an object.
 * Returns an empty array if the field is not a picklist.
 */
export async function getPicklistValues(
  objectName: string,
  fieldName: string,
  signal?: AbortSignal
): Promise<string[]> {
  const describe = await describeObject(objectName, signal);
  const field = describe.fields.find((f) => f.name === fieldName);
  if (!field?.picklistValues) return [];
  return field.picklistValues.filter((p) => p.active).map((p) => p.value);
}

export function clearDescribeCache(): void {
  describeCache.clear();
}
